import { FunctionComponent } from 'react';
import { Keycard, KeycardStatus } from '@/models/keycard';
import { usePagination } from '@/hooks/usePagination';
import { Box, Typography } from '@mui/material';
import { StarjackTable } from '@/components/Table/v2/Table';
import { formatStarjackDate } from '@/utils/time';
import { ResendAction } from '@/pageContents/customers/orderDetails/ResendAction';
import { usePostResendMutation } from '@/api/v2/keycards';
import { useNotification } from '@starjack/features/notifications';
import { Order } from '@/models/order';

type OrderDetailsKeycardsTableProps = {
    keycards: Keycard[];
    order?: Order;
};

export const OrderDetailsKeycardsTable: FunctionComponent<
    OrderDetailsKeycardsTableProps
> = ({ order, keycards = [] }) => {
    const {
        currentItems: currentKeycards,
        itemsPerPage: keycardsPerPage,
        setNextPage: setKeycardsPage,
        page,
        countTotal,
        setItemsPerPage,
    } = usePagination({
        items: keycards,
        itemsPerPage: 10,
    });
    const [postKeycardResend, { isLoading }] = usePostResendMutation();
    const { addNotification } = useNotification();

    const onResend = (reason: string, keycard: Keycard) => {
        postKeycardResend({
            keycardId: keycard.id,
            reason,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Keycard wurde neu versendet',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim neu senden. Bitte versuchen Sie es später noch einmal',
                });
            });
    };

    return (
        <Box>
            <Typography
                sx={{
                    mb: 4,
                }}
                variant='h5'
            >
                Keycards
            </Typography>

            <StarjackTable<Keycard>
                count={countTotal}
                items={currentKeycards}
                currentPage={page}
                onPageChange={setKeycardsPage}
                rowsPerPage={keycardsPerPage}
                onRowsPerPageChange={setItemsPerPage}
                itemId='id'
                columns={[
                    {
                        label: 'Keycard ID',
                        itemKey: 'id',
                    },
                    {
                        label: 'Titel',
                        itemKey: 'title',
                    },
                    {
                        label: 'Vorname',
                        itemKey: 'firstname',
                    },
                    {
                        label: 'Nachname',
                        itemKey: 'lastname',
                    },
                    {
                        label: 'Geschlecht',
                        itemKey: 'gender',
                    },
                    {
                        label: 'Geburtsdatum',
                        itemTransform: (keycard) =>
                            formatStarjackDate(keycard.birthday),
                    },
                    {
                        label: 'Produktionsdatum',
                        itemTransform: (keycard) =>
                            formatStarjackDate(keycard.productionDate),
                    },
                    {
                        label: 'Ablaufdatum',
                        itemTransform: (keycard) =>
                            formatStarjackDate(keycard.expirationDate),
                    },

                    {
                        label: 'Löschdatum',
                        itemTransform: (keycard) =>
                            formatStarjackDate(keycard.deletionDate),
                    },

                    {
                        label: 'Seriennummer',
                        itemKey: 'serialNumber',
                    },

                    {
                        label: 'Bild aktualisiert am',
                        itemTransform: (keycard) =>
                            keycard.imageLastUpdatedAt
                                ? formatStarjackDate(keycard.imageLastUpdatedAt)
                                : '-',
                        options: {
                            headerCellProps: {
                                sx: {
                                    '> div ': {
                                        width: '160px',
                                    },
                                },
                            },
                        },
                    },

                    {
                        label: 'Gekauft via',
                        itemTransform: (keycard) => {
                            if (order) {
                                return order.integrationRef ? 'Shop' : 'Portal';
                            }
                            return keycard.integrationRef ? 'Shop' : 'Portal';
                        },
                    },

                    {
                        label: 'Status',
                        itemKey: 'status',
                    },

                    {
                        label: 'Aktion',
                        itemTransform: (keycard) =>
                            keycard.status === KeycardStatus.PRODUCED ? (
                                <ResendAction
                                    onResend={(reason) =>
                                        onResend(reason, keycard)
                                    }
                                    isLoading={isLoading}
                                    buttonLabel='Keycard neu versenden'
                                />
                            ) : (
                                '-'
                            ),
                        options: {
                            rowCellProps: {
                                align: 'center',
                            },
                        },
                    },
                ]}
            />
        </Box>
    );
};
